import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./UserSearchSelect.css";



const CODE_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});


const ROW_HEIGHT = 32;
const DROPDOWN_VIEWPORT = 240;
const ROW_OVERSCAN = 6;
const VISIBLE_ROW_COUNT =
  Math.ceil(DROPDOWN_VIEWPORT / ROW_HEIGHT) + ROW_OVERSCAN * 2;

export const getUserDisplayName = (u) =>
  `${u?.first_name || ""} ${u?.last_name || ""}`.trim() || u?.username || "-";

export default function UserSearchSelect({
  users = [],
  searchText,
  onSearchTextChange,
  selectedUsers,
  onSelectedUsersChange,
  onEnter,
}) {
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownScrollTop, setDropdownScrollTop] = useState(0);

 
  const preparedUsers = useMemo(() => {
    const prepared = users.map((u) => {
      const name = getUserDisplayName(u);

      return {
        ...u,
        display_name: name,
        search_text: `${name} ${u.employee_code || ""}`.toLowerCase(),
      };
    });

    prepared.sort((a, b) =>
      CODE_COLLATOR.compare(
        String(a.employee_code || ""),
        String(b.employee_code || ""),
      ),
    );

    return prepared;
  }, [users]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

 
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [selectedUsers.length]);

 
  useEffect(() => {
    const track = scrollRef.current;
    if (!track) return;

    const handleWheel = (e) => {
      if (track.scrollWidth <= track.clientWidth || !e.deltaY) return;

      e.preventDefault();
      track.scrollLeft += e.deltaY;
    };

    track.addEventListener("wheel", handleWheel, { passive: false });
    return () => track.removeEventListener("wheel", handleWheel);
  }, []);

  const deferredSearchText = useDeferredValue(searchText);
  const deferredQuery = deferredSearchText.trim().toLowerCase();

  const suggestedUsers = useMemo(
    () =>
      deferredQuery
        ? preparedUsers.filter((u) => u.search_text.includes(deferredQuery))
        : preparedUsers,
    [preparedUsers, deferredQuery],
  );

  const visibleRange = useMemo(() => {
    const maxStart = Math.max(0, suggestedUsers.length - VISIBLE_ROW_COUNT);

    const start = Math.min(
      maxStart,
      Math.max(0, Math.floor(dropdownScrollTop / ROW_HEIGHT) - ROW_OVERSCAN),
    );

    return {
      start,
      users: suggestedUsers.slice(start, start + VISIBLE_ROW_COUNT),
    };
  }, [suggestedUsers, dropdownScrollTop]);

  const selectedIds = useMemo(
    () => new Set(selectedUsers.map((u) => u.id)),
    [selectedUsers],
  );

 
  useEffect(() => {
    setDropdownScrollTop(0);

    if (dropdownRef.current) {
      dropdownRef.current.scrollTop = 0;
    }
  }, [deferredQuery]);

  const openDropdown = () => {
    if (!showDropdown) {
      setDropdownScrollTop(0);

      if (dropdownRef.current) {
        dropdownRef.current.scrollTop = 0;
      }
    }

    setShowDropdown(true);
  };

  const toggleSelectUser = (u) => {
    onSelectedUsersChange(
      selectedIds.has(u.id)
        ? selectedUsers.filter((p) => p.id !== u.id)
        : [
            ...selectedUsers,
            {
              id: u.id,
              name: u.display_name,
              employee_code: u.employee_code || "",
            },
          ],
    );

   
    onSearchTextChange("");
    inputRef.current?.focus();
  };

  const removeSelectedUser = (id) => {
    onSelectedUsersChange(selectedUsers.filter((p) => p.id !== id));
  };

  const handleClear = () => {
    onSearchTextChange("");
    onSelectedUsersChange([]);
  };

  return (
    <div className="user-search-select-field" ref={searchRef}>
      <label>Search User</label>

      <div
        className="user-search-select-wrapper"
        onClick={(event) => {
          if (event.target.closest("button")) return;
          inputRef.current?.focus();
          openDropdown();
        }}
      >
        <svg
          className="user-search-select-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>

        <div className="user-search-select-scroll" ref={scrollRef}>
          {selectedUsers.map((u) => (
            <span key={u.id} className="user-search-select-chip">
              {u.name}
              <button
                type="button"
                className="user-search-select-chip-remove"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => removeSelectedUser(u.id)}
                aria-label={`Remove ${u.name}`}
              >
                ×
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            type="text"
            className="user-search-select-input"
            placeholder={selectedUsers.length > 0 ? "" : "Search members..."}
            value={searchText}
            onChange={(e) => {
              onSearchTextChange(e.target.value);
              openDropdown();
            }}
            onFocus={openDropdown}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setShowDropdown(false);
                onEnter?.();
              }
            }}
          />
        </div>

        {(searchText || selectedUsers.length > 0) && (
          <button
            type="button"
            className="user-search-select-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            aria-label="Clear search"
          >
            ×
          </button>
        )}

        {showDropdown && (
          <div
            className="user-search-select-dropdown"
            ref={dropdownRef}
            onScroll={(e) => setDropdownScrollTop(e.currentTarget.scrollTop)}
          >
            {suggestedUsers.length > 0 ? (
              <ul
                className="user-search-select-dropdown-list"
                style={{ height: suggestedUsers.length * ROW_HEIGHT }}
              >
                {visibleRange.users.map((u, i) => (
                  <li
                    key={u.id}
                    className={`user-search-select-dropdown-item ${
                      selectedIds.has(u.id) ? "selected" : ""
                    }`}
                    style={{ top: (visibleRange.start + i) * ROW_HEIGHT }}
                    onMouseDown={(e) => {
                      // Keep focus in the input so the next name can be typed.
                      e.preventDefault();
                      toggleSelectUser(u);
                    }}
                  >
                    <span className="user-search-select-dropdown-name">
                      {u.display_name}
                      {u.employee_code ? ` | ${u.employee_code}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="user-search-select-dropdown-message">
                No users found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
